import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import {
  buildCantileverPageHostByPageNum,
  systemDocumentPagesForDocument,
} from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import React, { createContext, useContext, useMemo } from 'react'

interface CantileverPageHostsContextType {
  /** Dual-read: cantilever-document-page host id when tagged. */
  getCantileverPageHostId: (pageNumber: number) => string | undefined
}

interface CantileverPageHostsProviderProps {
  children: React.ReactNode
  documentId: string
  siteID: string
}

export const CantileverPageHostsProvider: React.FC<
  CantileverPageHostsProviderProps
> = ({ children, documentId, siteID }) => {
  const { getPipelineConfigScopedTags, isPipelineConfigScopeLoading } =
    usePipelineConfig()

  const systemPagesLoadOptions = useMemo(() => {
    if (isPipelineConfigScopeLoading || !documentId) {
      return undefined
    }
    return systemDocumentPagesForDocument(
      documentId,
      getPipelineConfigScopedTags({ site: siteID })
    )
  }, [
    documentId,
    getPipelineConfigScopedTags,
    isPipelineConfigScopeLoading,
    siteID,
  ])

  const { data: systemDocumentPages } = useGetRolodexEntriesByLoadOptions<
    SystemDocumentPageEntry[]
  >(systemPagesLoadOptions)

  const cantileverPageHostByPageNum = useMemo(
    () => buildCantileverPageHostByPageNum(systemDocumentPages ?? []),
    [systemDocumentPages]
  )

  const getCantileverPageHostId = useMemo(
    () => (page: number) => cantileverPageHostByPageNum.get(page),
    [cantileverPageHostByPageNum]
  )

  const contextValue = useMemo(
    () => ({ getCantileverPageHostId }),
    [getCantileverPageHostId]
  )

  return (
    <CantileverPageHostsContext.Provider value={contextValue}>
      {children}
    </CantileverPageHostsContext.Provider>
  )
}

const CantileverPageHostsContext = createContext<
  CantileverPageHostsContextType | undefined
>(undefined)

/**
 * Returns page-host resolution when inside a CantileverPageHostsProvider,
 * or null otherwise. Used by leaf image viewers for dual-read hosts.
 */
export const useCantileverPageHostsOptional =
  (): CantileverPageHostsContextType | null => {
    return useContext(CantileverPageHostsContext) ?? null
  }
